/**
 * Adapter Tela 6 Dashboard de Qualidade — wire (GET /v1/quality/metrics/*)
 * → view-models. Funções puras (espelha tela5-adapter).
 *
 * Achados R15 materializados (vs schema.prisma + quality.controller.ts +
 * quality-metrics-daily-aggregator.cron.ts):
 *   - Todos os campos de taxa são Prisma `Decimal` → STRING no wire → `Number()`.
 *   - Sentimento (avgSentiment/sentimentAvg/sentimentPostAvg) é Dec(4,2) escala
 *     -1..1 (cron usa thresholds ±0.2 na distribuição) → sent10() mapeia p/ 0-10 (D4).
 *   - Demais taxas Dec(5,4) fração 0-1 → pct() = Math.round(*100).
 *   - avgResolutionTimeMinutes: cron v1 grava 0 (placeholder); só há a média (sem
 *     categorias) → resolutionTime degrada para 1 linha "Média" ou vazio (honesto).
 *   - behaviorPostAttendance: cron v1 grava {} → postBehavior vazio (honesto).
 *   - Serviços fazem findMany cru (não agregam) → adapter agrega por chave na janela (D5).
 *   - Backend retorna só items → totais de agentes client-side (D7).
 *   - agentId humanizado p/ PT (D8); acceptanceRate null → "n/a".
 */
import type {
  AgentPerformanceRaw,
  BehavioralPatternRaw,
  OperatorQualityRaw,
  PlaybookDriftRaw,
  PlaybookQualityRaw,
  QualityGlobalRaw,
  SequenceFlagsRaw,
} from '../../types';
import type {
  AgentRowVM,
  AgentTotalsVM,
  BehavioralPatternCardVM,
  DerivasRowVM,
  OperatorQualityRowVM,
  PlaybookQualityRowVM,
  QualityGlobalsVM,
  SequenceFlagsVM,
} from '../../view-models';

// Dec(5,4) fração 0-1 → % inteiro.
const pct = (frac: string): number => Math.round(Number(frac) * 100);

/** D4 — sentimento Dec(4,2) escala -1..1 → 0-10 (1 casa). -1→0, 0→5, +1→10. */
export const sent10 = (s: string): number => Math.round(((Number(s) + 1) / 2) * 100) / 10;

function humanizeToken(t: string): string {
  return t.replace(/[-_]/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** Mapa dos 10 agentes nominais canônicos (ADR-036) → PT (D8). */
const AGENT_LABELS: Record<string, string> = {
  'routing-agent': 'Roteamento',
  'suggestion-agent': 'Sugestão',
  'conductor-agent': 'Condutor',
  'classification-agent': 'Classificação',
  'summary-agent': 'Resumo',
  'behavioral-agent': 'Comportamental',
  'transition-agent': 'Transição',
  'protection-agent': 'Proteção',
  'checklist-agent': 'Checklist',
  'tool-agent': 'Ferramentas',
};

/** agentId → PT; token desconhecido humaniza (nunca vazio). */
export function humanizeAgentId(id: string): string {
  return AGENT_LABELS[id] ?? humanizeToken(id);
}

/** Mapa de playbooks conhecidos → PT; token desconhecido humaniza. */
const PLAYBOOK_LABELS: Record<string, string> = {
  pb_suporte: 'Suporte',
  pb_cobranca: 'Cobrança',
  pb_vendas: 'Vendas',
  pb_vendas_consultiva: 'Vendas Consultiva',
  pb_reclamacao: 'Reclamação',
  pb_retencao: 'Retenção',
};

export function humanizePlaybook(id: string): string {
  return PLAYBOOK_LABELS[id] ?? humanizeToken(id.replace(/^pb_/, ''));
}

/** Tokens → string humana ("280k", "1.2M"); <1000 verbatim. */
export function humanizeTokens(n: number): string {
  if (n >= 1_000_000) return `${(Math.round(n / 100_000) / 10).toString()}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000).toString()}k`;
  return n.toString();
}

// Targets de reabertura (proxy de resolução). Backend não fornece target →
// thresholds de produto FE (banda honesta): warn quando se aproxima do target
// (dentro da margem) ou o ultrapassa.
const REOPEN_TARGET_7D = 10;
const REOPEN_TARGET_30D = 12;
const REOPEN_NEAR_MARGIN = 2; // pp abaixo do target já sinaliza "próximo"

// Comportamento pós-atendimento — ordem + label PT + tom por chave conhecida.
const POST_BEHAVIOR_META: { key: string; label: string; tone: 'good' | 'bad' | 'neutral' }[] = [
  { key: 'recommended', label: 'Recomendou', tone: 'good' },
  { key: 'returned', label: 'Voltou', tone: 'neutral' },
  { key: 'cancelled', label: 'Cancelou', tone: 'bad' },
  { key: 'noAction', label: 'Sem ação', tone: 'neutral' },
];

/**
 * Métricas globais. Escolhe a linha mais recente da janela (por `date`).
 * resolutionTime/postBehavior degradam honestos quando o cron não populou.
 */
export function adaptGlobals(rows: QualityGlobalRaw[]): QualityGlobalsVM | null {
  if (rows.length === 0) return null;
  const row = [...rows].sort((a, b) => b.date.localeCompare(a.date))[0]!;

  const sevenDays = pct(row.reopeningRate7d);
  const thirtyDays = pct(row.reopeningRate30d);

  // Tempo de resolução: só há a média (cron v1 grava 0). NÃO inventar categorias.
  const resolutionTime =
    row.avgResolutionTimeMinutes > 0
      ? [{ label: 'Média', minutes: row.avgResolutionTimeMinutes }]
      : [];

  // Comportamento pós-atendimento: mapeia só as chaves presentes no Json.
  const postBehavior = POST_BEHAVIOR_META.filter(
    (m) => row.behaviorPostAttendance[m.key] !== undefined,
  ).map((m) => ({
    label: m.label,
    pct: Math.round(row.behaviorPostAttendance[m.key]! * 100),
    tone: m.tone,
  }));

  return {
    sentiment: {
      value: sent10(row.avgSentiment),
      distribution: {
        positive: Math.round(row.sentimentDistribution.positive * 100),
        neutral: Math.round(row.sentimentDistribution.neutral * 100),
        negative: Math.round(row.sentimentDistribution.negative * 100),
      },
    },
    reopen: {
      sevenDays,
      thirtyDays,
      target7: REOPEN_TARGET_7D,
      target30: REOPEN_TARGET_30D,
      status7: sevenDays >= REOPEN_TARGET_7D - REOPEN_NEAR_MARGIN ? 'warn' : 'ok',
      status30: thirtyDays >= REOPEN_TARGET_30D - REOPEN_NEAR_MARGIN ? 'warn' : 'ok',
    },
    resolutionTime,
    postBehavior,
  };
}

// ─── Performance por atendente ─────────────────────────────────────────────

// Bandas at-risk (FE, honesto): pendências vencidas ≥3 OU SLA <90% OU sentimento <6.5.
const AT_RISK_OVERDUE = 3;
const AT_RISK_SLA = 90;
const AT_RISK_SENT = 6.5;

export function adaptOperatorRow(
  r: OperatorQualityRaw,
  nameFor: (userId: string) => string,
): OperatorQualityRowVM {
  const sent = sent10(r.sentimentAvg);
  const sla = pct(r.slaCompliance);
  return {
    userId: r.operatorUserId,
    name: nameFor(r.operatorUserId),
    conv: r.conversationCount,
    sent,
    sla,
    ia: pct(r.suggestionAcceptRate),
    pendResolved: r.pendenciesResolved,
    pendOverdue: r.pendenciesOverdue,
    atRisk: r.pendenciesOverdue >= AT_RISK_OVERDUE || sla < AT_RISK_SLA || sent < AT_RISK_SENT,
  };
}

/** Agrega por operatorUserId (soma counts; média ponderada por conversationCount). */
export function adaptOperators(
  rows: OperatorQualityRaw[],
  nameFor: (userId: string) => string | undefined,
): OperatorQualityRowVM[] {
  const byOp = new Map<string, OperatorQualityRaw[]>();
  for (const r of rows) {
    const list = byOp.get(r.operatorUserId) ?? [];
    list.push(r);
    byOp.set(r.operatorUserId, list);
  }
  return Array.from(byOp.entries()).map(([userId, list]) => {
    const conv = list.reduce((a, r) => a + r.conversationCount, 0);
    const w = (sel: (r: OperatorQualityRaw) => number): number =>
      conv > 0 ? list.reduce((a, r) => a + sel(r) * r.conversationCount, 0) / conv : 0;
    const sentAvg = w((r) => Number(r.sentimentAvg));
    const slaAvg = w((r) => Number(r.slaCompliance));
    const iaAvg = w((r) => Number(r.suggestionAcceptRate));
    const pendOverdue = list.reduce((a, r) => a + r.pendenciesOverdue, 0);
    const sent = Math.round(((sentAvg + 1) / 2) * 100) / 10;
    const sla = Math.round(slaAvg * 100);
    return {
      userId,
      // Nome do Engine (by-operator) > presence > placeholder honesto (nunca id cru).
      name: list[0]?.operatorName ?? nameFor(userId) ?? '—',
      conv,
      sent,
      sla,
      ia: Math.round(iaAvg * 100),
      pendResolved: list.reduce((a, r) => a + r.pendenciesResolved, 0),
      pendOverdue,
      atRisk: pendOverdue >= AT_RISK_OVERDUE || sla < AT_RISK_SLA || sent < AT_RISK_SENT,
    };
  });
}

// ─── Performance por playbook ──────────────────────────────────────────────

const PLAYBOOK_ATTENTION = 85; // conclusão < 85% → ⚠

export function adaptPlaybookRow(r: PlaybookQualityRaw): PlaybookQualityRowVM {
  const conclusao = pct(r.conclusionRate);
  return {
    playbookId: r.playbookId,
    name: humanizePlaybook(r.playbookId),
    uso: r.usageCount,
    conclusao,
    sentPos: sent10(r.sentimentPostAvg),
    tempoMin: r.durationAvgMinutes,
    attention: conclusao < PLAYBOOK_ATTENTION,
  };
}

/** Agrega por playbookId (soma uso; média ponderada por usageCount). */
export function adaptPlaybooks(rows: PlaybookQualityRaw[]): PlaybookQualityRowVM[] {
  const byPb = new Map<string, PlaybookQualityRaw[]>();
  for (const r of rows) {
    const list = byPb.get(r.playbookId) ?? [];
    list.push(r);
    byPb.set(r.playbookId, list);
  }
  return Array.from(byPb.entries()).map(([playbookId, list]) => {
    const uso = list.reduce((a, r) => a + r.usageCount, 0);
    const w = (sel: (r: PlaybookQualityRaw) => number): number =>
      uso > 0 ? list.reduce((a, r) => a + sel(r) * r.usageCount, 0) / uso : 0;
    const conclusao = Math.round(w((r) => Number(r.conclusionRate)) * 100);
    const sentPost = w((r) => Number(r.sentimentPostAvg));
    const tempo =
      uso > 0
        ? Math.round(list.reduce((a, r) => a + r.durationAvgMinutes * r.usageCount, 0) / uso)
        : 0;
    return {
      playbookId,
      name: humanizePlaybook(playbookId),
      uso,
      conclusao,
      sentPos: Math.round(((sentPost + 1) / 2) * 100) / 10,
      tempoMin: tempo,
      attention: conclusao < PLAYBOOK_ATTENTION,
    };
  });
}

// ─── Performance por Agente Nominal ────────────────────────────────────────

export function adaptAgentRow(r: AgentPerformanceRaw): AgentRowVM {
  return {
    id: r.agentId,
    label: humanizeAgentId(r.agentId),
    inv: r.invocations,
    tokens: humanizeTokens(r.tokensTotal),
    accept: r.acceptanceRate !== null ? pct(r.acceptanceRate) : null,
    lat: Math.round(r.avgLatencyMs / 100) / 10,
  };
}

/**
 * Agrega por agentId quando a janela traz várias linhas (soma invocations/
 * tokens; média ponderada de acceptanceRate por invocations). Totais
 * client-side (D7): Σ invocações.
 *
 * B-1 (17/Set/2026): `costTotalUsd` saiu daqui. Era o custo da PLATAFORMA, e o
 * backend parou de devolvê-lo — auditoria de escopo plataforma × tenant.
 */
export function adaptAgents(rows: AgentPerformanceRaw[]): {
  rows: AgentRowVM[];
  totals: AgentTotalsVM;
} {
  const byAgent = new Map<string, AgentPerformanceRaw[]>();
  for (const r of rows) {
    const list = byAgent.get(r.agentId) ?? [];
    list.push(r);
    byAgent.set(r.agentId, list);
  }
  const vmRows = Array.from(byAgent.entries()).map(([agentId, list]): AgentRowVM => {
    const inv = list.reduce((a, r) => a + r.invocations, 0);
    const tokens = list.reduce((a, r) => a + r.tokensTotal, 0);
    const lat = list.reduce((a, r) => a + r.avgLatencyMs, 0) / Math.max(list.length, 1);
    // Aceite ponderado por invocações apenas sobre linhas com aceite presente.
    const withAccept = list.filter((r) => r.acceptanceRate !== null);
    const acceptInv = withAccept.reduce((a, r) => a + r.invocations, 0);
    const accept =
      withAccept.length > 0 && acceptInv > 0
        ? Math.round(
            (withAccept.reduce((a, r) => a + Number(r.acceptanceRate) * r.invocations, 0) /
              acceptInv) *
              100,
          )
        : null;
    return {
      id: agentId,
      label: humanizeAgentId(agentId),
      inv,
      tokens: humanizeTokens(tokens),
      accept,
      lat: Math.round(lat / 100) / 10,
    };
  });
  return {
    rows: vmRows,
    totals: {
      invocacoes: vmRows.reduce((a, r) => a + r.inv, 0),
    },
  };
}

// ─── Seção 5 — Análise de Sequences (D1) ───────────────────────────────────

/** Map direto dos 4 counts reais + total. Os breakdowns (top atendentes /
 *  top Bonds) NÃO vêm do endpoint → placeholder honesto na página (Item 328). */
export function adaptSequences(raw: SequenceFlagsRaw): SequenceFlagsVM {
  return {
    highVolume: raw.highVolumeCount,
    escalating: raw.escalatingSentimentCount,
    allAbandoned: raw.allAbandonedCount,
    crossChannel: raw.crossChannelInsistenceCount,
    total: raw.totalSequences,
  };
}

// ─── Seção 6 — Postura Comportamental ──────────────────────────────────────

/** patternType (8 enums) → PT. Token desconhecido humaniza (nunca vazio). */
const PATTERN_TYPE_LABELS: Record<string, string> = {
  defensive_tone: 'tom defensivo',
  interrupting: 'interrupções frequentes',
  lack_of_empathy: 'falta de empatia',
  slow_response: 'resposta lenta',
  script_deviation: 'desvio de roteiro',
  aggressive_tone: 'tom agressivo',
  inappropriate_humor: 'humor inadequado',
  lack_of_validation: 'falta de validação',
};

export function humanizePatternType(t: string): string {
  return PATTERN_TYPE_LABELS[t] ?? humanizeToken(t).toLowerCase();
}

const SEVERITY_ORDER: Record<BehavioralPatternCardVM['severity'], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

/** Card único. weeks = ceil((last - first)/7d), mínimo 1. */
export function adaptBehavioralCard(
  p: BehavioralPatternRaw,
  nameFor: (userId: string) => string | undefined,
): BehavioralPatternCardVM {
  const spanMs = new Date(p.lastDetectedAt).getTime() - new Date(p.firstDetectedAt).getTime();
  const weeks = Math.max(1, Math.ceil(spanMs / MS_PER_WEEK));
  const trend = p.trend === 'increasing' ? 'up' : p.trend === 'decreasing' ? 'down' : 'flat';
  return {
    id: p.id,
    name: nameFor(p.operatorUserId) ?? '—',
    pattern: humanizePatternType(p.patternType),
    severity: p.severity,
    trend,
    weeks,
  };
}

/** Filtra só `status==='active'` (degradação honesta) e ordena por severidade desc. */
export function adaptBehavioralCards(
  rows: BehavioralPatternRaw[],
  nameFor: (userId: string) => string | undefined,
): BehavioralPatternCardVM[] {
  return rows
    .filter((p) => p.status === 'active')
    .map((p) => adaptBehavioralCard(p, nameFor))
    .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}

// ─── Seção 7 — Derivas (Eixo P) — pivot do array flat (D2) ─────────────────

/** Colunas/linhas canônicas do mockup `DerivasMatrix`. */
const DERIVAS_CATEGORIES = ['Suporte', 'Cobrança', 'Vendas', 'Reclamação', 'Retenção'] as const;

/**
 * Pivota o array flat `playbook-drift` para a matriz 5×5 das categorias
 * canônicas. Humaniza playbookId→categoria via `humanizePlaybook`; transições
 * cujo from/to não cai numa das 5 categorias são ignoradas (honesto — não
 * força 5 fixas com dado inventado). Diagonal (from===to) → '-'; célula sem
 * transição → 0; from/to repetidos somam transitionCount.
 */
export function adaptDerivas(rows: PlaybookDriftRaw[]): DerivasRowVM[] {
  const isCanonical = (label: string): label is (typeof DERIVAS_CATEGORIES)[number] =>
    (DERIVAS_CATEGORIES as readonly string[]).includes(label);

  // Acumula transitionCount por (fromCat, toCat) canônicas.
  const acc = new Map<string, number>();
  for (const r of rows) {
    if (r.fromPlaybookId === null) continue;
    const from = humanizePlaybook(r.fromPlaybookId);
    const to = humanizePlaybook(r.toPlaybookId);
    if (!isCanonical(from) || !isCanonical(to)) continue;
    const key = `${from}→${to}`;
    acc.set(key, (acc.get(key) ?? 0) + r.transitionCount);
  }

  return DERIVAS_CATEGORIES.map((from) => {
    const to: Record<string, number | '-'> = {};
    for (const col of DERIVAS_CATEGORIES) {
      to[col] = from === col ? '-' : (acc.get(`${from}→${col}`) ?? 0);
    }
    return { from, to };
  });
}
